
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { formatGBP, formatDate } from "@/lib/formatters";

export type ProjectType = {
  id: string;
  name: string;
  venue_name?: string;
  status: "planning" | "live" | "closed";
  start_date?: string;
  end_date?: string;
  capex_estimate?: number;
  forecast_revenue_yr1?: number;
  opex_estimate?: number;
  image_url?: string;
};

interface ProjectCardProps {
  project: ProjectType;
  onClick?: () => void;
}

export const ProjectCard = ({
  project,
  onClick
}: ProjectCardProps) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case "planning":
        return "bg-amber-100 text-amber-800 hover:bg-amber-200";
      case "live":
        return "bg-green-100 text-green-800 hover:bg-green-200";
      case "closed":
        return "bg-gray-100 text-gray-800 hover:bg-gray-200";
      default:
        return "bg-gray-100 text-gray-800 hover:bg-gray-200";
    }
  };

  // Debug logging
  console.log('ProjectCard rendering with capex_estimate:', project.capex_estimate);
  
  return <Card className="overflow-hidden border border-border hover:shadow-md transition-shadow cursor-pointer group" onClick={onClick}>
      {project.image_url ? (
        <div className="h-40 w-full overflow-hidden">
          <img 
            src={project.image_url}
            alt={project.name}
            className="w-full h-full object-cover"
            onError={(e) => {
              // Handle image loading error by setting a fallback
              console.log("Image load error for:", project.name);
              e.currentTarget.src = "/placeholder.svg"; 
            }}
          />
        </div>
      ) : null}
      <CardHeader className={`p-4 bg-cs-purple text-white ${!project.image_url ? "" : "border-t border-border"}`}>
        <div className="flex justify-between items-start">
          <div>
            <h3 className="text-lg font-bold line-clamp-2 text-slate-50 group-hover:text-white">{project.name}</h3>
            {project.venue_name && <p className="text-sm text-slate-50 group-hover:text-white">{project.venue_name}</p>}
          </div>
          <Badge className={`${getStatusColor(project.status)} capitalize`}>
            {project.status}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="p-4 space-y-4">
        {(project.start_date || project.end_date) && <div className="grid grid-cols-2 gap-2 text-sm">
            {project.start_date && <div>
                <p className="text-muted-foreground">Start Date</p>
                <p className="font-medium">{formatDate(project.start_date)}</p>
              </div>}
            {project.end_date && <div>
                <p className="text-muted-foreground">End Date</p>
                <p className="font-medium">{formatDate(project.end_date)}</p>
              </div>}
          </div>}
      </CardContent>
      <CardFooter className="p-4 pt-0 border-t">
        <div className="w-full">
          {project.capex_estimate !== undefined && <div className="flex justify-between items-center text-sm">
              <span className="text-muted-foreground">Budget:</span>
              <span className="font-bold">
                {formatGBP(project.capex_estimate)}
              </span>
            </div>}
        </div>
      </CardFooter>
    </Card>;
};
